import { useContext } from "react";

import { Box } from "ui/box";
import { Stack } from "ui/stack";
import { Divider } from "ui/divider";
import { Collapse } from "ui/collapse";
import TruncatedText from "components/partials/truncated-text/truncated-text";
import { CommentIcons } from "containers/admin/clients/touchpoint/components/comments/comment-icons/comment-icons";
import { CommentReplyDisplay } from "containers/admin/clients/touchpoint/components/comments/comment-reply-display/comment-reply-display";
import { CommentDisplayByline } from "containers/admin/clients/touchpoint/components/comments/comment-display-by-line/comment-display-by-line";

import CommentContext from "contexts/comment-context";

import { useCurrentUser } from "hooks/api/use-current-user";

import { formatDateTime } from "utilities/date";
import { hasPermission } from "utilities/user";

import { Permission } from "types/auth";
import { Comment, CommentReply } from "types/comments";
import CommentActionsCard from "../comment-actions-card/comment-actions-card";

interface CommentDisplayProps {
  comment: Comment;
  isClientUser?: boolean;
  replies: CommentReply[];
}

export const AnnotationCommentDisplay = ({
  comment,
  isClientUser,
  replies,
}: CommentDisplayProps) => {
  const { updateComment } = useContext(CommentContext);
  const currentUser = useCurrentUser();
  const canComment = hasPermission(currentUser, Permission.PERM_COMMENT_WRITE);

  const toggleOpenComment = () => {
    const isFlagged = comment.open ? !comment.actionItem : false;
    updateComment({ ...comment, open: !comment.open, actionItem: isFlagged });
  };
  const toggleFlagComment = () => {
    updateComment({ ...comment, actionItem: !comment.actionItem });
  };

  const CommentDisplayHeader = () => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        fontSize: "0.75rem",
        letterSpacing: "0.025em",
        py: "0.75rem",
      }}>
      <Box component="p">
        {comment.version && (
          <>
            <span>v{comment.version}</span>
            <span className="mx-1">•</span>
          </>
        )}
        {formatDateTime(comment.createdDate)}
      </Box>

      <CommentIcons
        canOpen={canComment && !isClientUser}
        canFlag={canComment && comment.open}
        isChecked={!comment.open}
        isFlagged={comment.actionItem}
        sx={{ ml: "auto" }}
        toggleFlag={toggleFlagComment}
        toggleCheck={toggleOpenComment}
      />
    </Box>
  );

  return (
    <Box sx={{ px: "0.5rem", pb: 0 }}>
      <CommentDisplayHeader />

      <Box sx={{ opacity: comment.open ? 1 : 0.5 }}>
        <CommentDisplayByline user={comment.user} />
        <Stack
          spacing={1}
          divider={<Divider sx={{ borderColor: "grey.200" }} />}
          className="content"
          sx={{ pl: "1.5rem" }}>
          <TruncatedText text={comment.text} noOfLines={4} initialTruncate={true} />

          <CommentActionsCard
            isClientUser={!!isClientUser}
            comment={comment}
            setReplyInputOpen={(isOpen) => !isOpen}
          />

          {/* Comment Replies */}
          {comment.replyCount > 0 && (
            <Box>
              <Stack spacing={1} sx={{ mb: "0.5rem" }}>
                {replies.map((reply: CommentReply) => (
                  // Per reply, so one arriving in `replies` after mount still grows in.
                  <Collapse key={reply.id} in appear>
                    <CommentReplyDisplay reply={reply} />
                  </Collapse>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
